import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Settings2 } from "lucide-react";
import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@eduviaos/ui-kit";
import { useApplyPortalConfig } from "@/hooks/useApplyPortalConfig";

/**
 * Phase H2 FE — IA dashboard status chip for the public apply portal.
 *
 * Renders an inline pill on the Admissions page (and anywhere else
 * the IA needs at-a-glance visibility) that flips between:
 *   - "Apply portal — open now" (emerald)
 *   - "Apply portal — closed (next: <human-friendly window>)" (muted)
 *
 * Clicking the chip opens the lifecycle settings page so admins can
 * adjust the schedule without hunting through Settings. Loading and
 * error states render as a neutral skeleton-like pill so the surface
 * never flashes empty.
 */
export function ApplyPortalStatusChip() {
  const cfg = useApplyPortalConfig();

  if (cfg.isLoading) {
    return (
      <Badge
        variant="secondary"
        className="bg-muted text-muted-foreground border-border h-6 animate-pulse px-2 py-0.5"
        aria-label="Loading apply portal status"
      >
        Apply portal …
      </Badge>
    );
  }
  if (!cfg.data) {
    return null;
  }

  const isOpen = cfg.data.is_open_now;
  const next = cfg.data.next_window;

  const fmtCompact = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const tooltipText = isOpen
    ? "The public apply portal is accepting new enquiries."
    : next
      ? `Closed. Next window opens ${fmtCompact(next.start_at)} → ${fmtCompact(next.end_at)}.`
      : "Closed. No future windows scheduled — open Settings → Admissions lifecycle to schedule one.";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/settings/admissions-lifecycle"
            className="focus-visible:ring-ring inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            data-testid="apply-portal-status-chip"
            aria-label={tooltipText}
          >
            <Badge
              variant="secondary"
              className={
                isOpen
                  ? "inline-flex items-center gap-1 border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-emerald-700 dark:text-emerald-300"
                  : "border-border bg-muted text-muted-foreground inline-flex items-center gap-1 px-2 py-0.5"
              }
            >
              {isOpen ? (
                <CheckCircle2 aria-hidden="true" className="size-3" />
              ) : (
                <Clock aria-hidden="true" className="size-3" />
              )}
              <span className="text-xs font-medium">Apply portal:</span>
              <span className="text-xs">
                {isOpen ? "open" : next ? `opens ${fmtCompact(next.start_at)}` : "closed"}
              </span>
              <Settings2 aria-hidden="true" className="size-3 opacity-60" />
            </Badge>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
